import React, { useState, useEffect } from 'react'
import { useSearchStore } from '../../../store/jpeTopPageStore'
import { ImageCarousel } from '../nureseries/ImageCarousel'
import { nurseryNameWithPrefecture } from '../utils'

export const FloatingNurseryItem: React.FC = () => {
  const {
    selectedNursery: nursery,
    selectedPinPosition,
    selectedMarkerRef,
    setSelectedPinPosition,
    clearSelection,
    isDesktop,
    mapContainerRef,
  } = useSearchStore()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [cardPosition, setCardPosition] = useState<{ left: number; top: number } | null>(null)

  // Trigger slide-up animation on mount
  useEffect(() => {
    setIsAnimating(true)
  }, [])

  // Update position when page scrolls (desktop only)
  useEffect(() => {
    if (!isDesktop || !selectedMarkerRef) return

    const updatePosition = () => {
      const rect = selectedMarkerRef.getBoundingClientRect()
      const position = {
        x: rect.left + rect.width / 2,
        y: rect.top,
      }
      setSelectedPinPosition(position)
    }

    // Use capture phase to catch scroll events from any scrollable container
    window.addEventListener('scroll', updatePosition, true)
    return () => window.removeEventListener('scroll', updatePosition, true)
  }, [isDesktop, selectedMarkerRef, setSelectedPinPosition])

  // Calculate card position on desktop when pin position changes
  useEffect(() => {
    if (isDesktop && selectedPinPosition && mapContainerRef) {
      const mapBounds = mapContainerRef.getBoundingClientRect()

      // Check if marker is outside map bounds
      if (
        selectedPinPosition.x < mapBounds.left ||
        selectedPinPosition.x > mapBounds.right ||
        selectedPinPosition.y < mapBounds.top ||
        selectedPinPosition.y > mapBounds.bottom
      ) {
        clearSelection()
        return
      }

      const cardWidth = 360
      const cardHeight = 300
      const horizontalOffset = 10
      const verticalOffset = 40

      // Calculate VISIBLE map bounds (intersection with viewport)
      const visibleTop = Math.max(mapBounds.top, 0)
      const visibleBottom = Math.min(mapBounds.bottom, window.innerHeight)
      const visibleLeft = Math.max(mapBounds.left, 0)
      const visibleRight = Math.min(mapBounds.right, window.innerWidth)

      // Calculate middle point for horizontal positioning
      const visibleMiddleX = visibleLeft + (visibleRight - visibleLeft) / 2

      // Horizontal positioning based on VISIBLE middle
      let left: number
      if (selectedPinPosition.x < visibleMiddleX) {
        // Marker in left half of VISIBLE area → show card to RIGHT
        left = selectedPinPosition.x + horizontalOffset
      } else {
        // Marker in right half of VISIBLE area → show card to LEFT
        left = selectedPinPosition.x - cardWidth - horizontalOffset
      }

      // Clamp to VISIBLE bounds
      left = Math.max(visibleLeft + 20, Math.min(left, visibleRight - cardWidth - 20))

      // Vertical positioning - check available space in each direction
      const spaceAbove = selectedPinPosition.y - visibleTop
      const spaceBelow = visibleBottom - selectedPinPosition.y
      const neededSpaceAbove = cardHeight + verticalOffset + 20
      const neededSpaceBelow = cardHeight + verticalOffset + 20

      // Vertical positioning - always prefer showing ABOVE the marker
      let top: number
      if (spaceAbove >= neededSpaceAbove) {
        // Enough space above → show above
        top = selectedPinPosition.y - cardHeight - verticalOffset
      } else if (spaceBelow >= neededSpaceBelow) {
        // Not enough above, but enough below → show below
        top = selectedPinPosition.y + verticalOffset
      } else {
        // Not enough space in either direction → position at top of visible area
        top = visibleTop + 20
      }

      // Final clamp to ensure card stays within visible bounds
      top = Math.max(visibleTop + 20, Math.min(top, visibleBottom - cardHeight - 20))

      setCardPosition({ left, top })
    } else {
      setCardPosition(null)
    }
  }, [isDesktop, selectedPinPosition, mapContainerRef, clearSelection])

  const handleIndexChange = (index: number) => {
    setCurrentImageIndex(index)
  }

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    clearSelection()
  }

  const handleCardClick = () => {
    if (!nursery.lpUrl) {
      return
    }
    window.open(nursery.lpUrl, '_blank')
  }

  if (!nursery) {
    return null
  }

  // Desktop positioning near pin, mobile centered at bottom
  const cardStyle =
    isDesktop && cardPosition
      ? {
          position: 'fixed' as const,
          left: `${cardPosition.left}px`,
          top: `${cardPosition.top}px`,
          width: '360px',
          opacity: isAnimating ? 1 : 0,
          transform: isAnimating ? 'scale(1)' : 'scale(0.95)',
          borderRadius: '12px',
          zIndex: 100000000,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 15px -5px rgba(0, 0, 0, 0.2)',
          transition: 'left 0.05s ease-out, top 0.05s ease-out',
        }
      : {
          position: 'fixed' as const,
          bottom: '20px',
          left: '50%',
          transform: isAnimating ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(150%)',
          width: '90%',
          maxWidth: '90vw',
          borderRadius: '12px',
          zIndex: 100000000,
          transition: 'transform 0.3s ease-out',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 15px -5px rgba(0, 0, 0, 0.2)',
        }

  return (
    <div className="bg-white overflow-hidden" style={cardStyle} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={handleCloseClick}
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-lg hover:bg-gray-100 transition-colors z-10"
        style={{
          top: '8px',
          right: '8px',
        }}
        aria-label="Close"
      >
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="16" fill="white" />
          <path
            d="M10.0265 23.4665L8.5332 21.9732L14.5065 15.9999L8.5332 10.0265L10.0265 8.5332L15.9999 14.5065L21.9732 8.5332L23.4665 10.0265L17.4932 15.9999L23.4665 21.9732L21.9732 23.4665L15.9999 17.4932L10.0265 23.4665Z"
            fill="#8B9096"
          />
        </svg>
      </button>

      {/* Image Carousel Section */}
      <div style={{ borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
        <ImageCarousel
          images={nursery.keyVisualImages}
          badge={nursery.badge}
          currentIndex={currentImageIndex}
          onIndexChange={handleIndexChange}
        />
      </div>

      <div className={`p-3 ${nursery.lpUrl ? 'cursor-pointer' : ''}`} onClick={handleCardClick}>
        <h3 className="font-size-18 text-gray-900 mb-2">{nurseryNameWithPrefecture(nursery)}</h3>
        <div className="flex items-start gap-2 mb-2">
          <svg width="19" height="18" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.83333 12.0625C7.20833 10.9514 8.24653 9.88194 8.94792 8.85417C9.64931 7.82639 10 6.86111 10 5.95833C10 4.70833 9.61111 3.68056 8.83333 2.875C8.05556 2.06944 7.05556 1.66667 5.83333 1.66667C4.61111 1.66667 3.61111 2.06944 2.83333 2.875C2.05556 3.68056 1.66667 4.70833 1.66667 5.95833C1.66667 6.86111 2.01736 7.82639 2.71875 8.85417C3.42014 9.88194 4.45833 10.9514 5.83333 12.0625ZM5.83333 14.1667C3.875 12.7222 2.41319 11.3194 1.44792 9.95833C0.482639 8.59722 0 7.26389 0 5.95833C0 4.22222 0.541667 2.79514 1.625 1.67708C2.70833 0.559028 4.11111 0 5.83333 0C7.55555 0 8.95833 0.559028 10.0417 1.67708C11.125 2.79514 11.6667 4.22222 11.6667 5.95833C11.6667 7.26389 11.184 8.59722 10.2187 9.95833C9.25347 11.3194 7.79167 12.7222 5.83333 14.1667ZM5.83333 7.5C6.29167 7.5 6.68403 7.33681 7.01042 7.01042C7.33681 6.68403 7.5 6.29167 7.5 5.83333C7.5 5.375 7.33681 4.98264 7.01042 4.65625C6.68403 4.32986 6.29167 4.16667 5.83333 4.16667C5.375 4.16667 4.98264 4.32986 4.65625 4.65625C4.32986 4.98264 4.16667 5.375 4.16667 5.83333C4.16667 6.29167 4.32986 6.68403 4.65625 7.01042C4.98264 7.33681 5.375 7.5 5.83333 7.5ZM0 16.6667V15H11.6667V16.6667H0Z"
              fill="#8B9096"
            />
          </svg>
          <span className="text-sm text-gray-600">{nursery.location}</span>
        </div>

        <div className="flex flex-col gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 15V13.6875C0 13.0903 0.305556 12.6042 0.916667 12.2292C1.52778 11.8542 2.33333 11.6667 3.33333 11.6667C3.51389 11.6667 3.6875 11.6701 3.85417 11.6771C4.02083 11.684 4.18056 11.7014 4.33333 11.7292C4.13889 12.0208 3.99306 12.3264 3.89583 12.6458C3.79861 12.9653 3.75 13.2986 3.75 13.6458V15H0ZM5 15V13.6458C5 13.2014 5.12153 12.7951 5.36458 12.4271C5.60764 12.059 5.95139 11.7361 6.39583 11.4583C6.84028 11.1806 7.37153 10.9722 7.98958 10.8333C8.60764 10.6944 9.27778 10.625 10 10.625C10.7361 10.625 11.4132 10.6944 12.0312 10.8333C12.6493 10.9722 13.1806 11.1806 13.625 11.4583C14.0694 11.7361 14.4097 12.059 14.6458 12.4271C14.8819 12.7951 15 13.2014 15 13.6458V15H5ZM16.25 15V13.6458C16.25 13.2847 16.2049 12.9444 16.1146 12.625C16.0243 12.3056 15.8889 12.0069 15.7083 11.7292C15.8611 11.7014 16.0174 11.684 16.1771 11.6771C16.3368 11.6701 16.5 11.6667 16.6667 11.6667C17.6667 11.6667 18.4722 11.8507 19.0833 12.2188C19.6944 12.5868 20 13.0764 20 13.6875V15H16.25ZM6.77083 13.3333H13.25C13.1111 13.0556 12.7257 12.8125 12.0938 12.6042C11.4618 12.3958 10.7639 12.2917 10 12.2917C9.23611 12.2917 8.53819 12.3958 7.90625 12.6042C7.27431 12.8125 6.89583 13.0556 6.77083 13.3333ZM3.33333 10.8333C2.875 10.8333 2.48264 10.6701 2.15625 10.3438C1.82986 10.0174 1.66667 9.625 1.66667 9.16667C1.66667 8.69444 1.82986 8.29861 2.15625 7.97917C2.48264 7.65972 2.875 7.5 3.33333 7.5C3.80556 7.5 4.20139 7.65972 4.52083 7.97917C4.84028 8.29861 5 8.69444 5 9.16667C5 9.625 4.84028 10.0174 4.52083 10.3438C4.20139 10.6701 3.80556 10.8333 3.33333 10.8333ZM16.6667 10.8333C16.2083 10.8333 15.816 10.6701 15.4896 10.3438C15.1632 10.0174 15 9.625 15 9.16667C15 8.69444 15.1632 8.29861 15.4896 7.97917C15.816 7.65972 16.2083 7.5 16.6667 7.5C17.1389 7.5 17.5347 7.65972 17.8542 7.97917C18.1736 8.29861 18.3333 8.69444 18.3333 9.16667C18.3333 9.625 18.1736 10.0174 17.8542 10.3438C17.5347 10.6701 17.1389 10.8333 16.6667 10.8333ZM10 10C9.30556 10 8.71528 9.75694 8.22917 9.27083C7.74306 8.78472 7.5 8.19444 7.5 7.5C7.5 6.79167 7.74306 6.19792 8.22917 5.71875C8.71528 5.23958 9.30556 5 10 5C10.7083 5 11.3021 5.23958 11.7812 5.71875C12.2604 6.19792 12.5 6.79167 12.5 7.5C12.5 8.19444 12.2604 8.78472 11.7812 9.27083C11.3021 9.75694 10.7083 10 10 10ZM10 8.33333C10.2361 8.33333 10.434 8.25347 10.5938 8.09375C10.7535 7.93403 10.8333 7.73611 10.8333 7.5C10.8333 7.26389 10.7535 7.06597 10.5938 6.90625C10.434 6.74653 10.2361 6.66667 10 6.66667C9.76389 6.66667 9.56597 6.74653 9.40625 6.90625C9.24653 7.06597 9.16667 7.26389 9.16667 7.5C9.16667 7.73611 9.24653 7.93403 9.40625 8.09375C9.56597 8.25347 9.76389 8.33333 10 8.33333Z"
                fill="#8B9096"
              />
            </svg>
            <span>{nursery.acceptClass} Class</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0007 18.3337C7.84787 18.3337 5.97982 17.6184 4.39648 16.1878C2.81315 14.7573 1.91732 12.9725 1.70898 10.8337H3.39648C3.60482 12.5142 4.33746 13.9066 5.5944 15.0107C6.85135 16.1149 8.3201 16.667 10.0007 16.667C11.8618 16.667 13.4382 16.0212 14.7298 14.7295C16.0215 13.4378 16.6673 11.8614 16.6673 10.0003C16.6673 8.13921 16.0215 6.56283 14.7298 5.27116C13.4382 3.97949 11.8618 3.33366 10.0007 3.33366C8.80621 3.33366 7.69857 3.6288 6.67773 4.21908C5.6569 4.80935 4.84787 5.62533 4.25065 6.66699H6.66732V8.33366H1.83398C2.23676 6.38921 3.20204 4.79199 4.72982 3.54199C6.2576 2.29199 8.01454 1.66699 10.0007 1.66699C11.1534 1.66699 12.2368 1.88574 13.2507 2.32324C14.2645 2.76074 15.1465 3.35449 15.8965 4.10449C16.6465 4.85449 17.2402 5.73644 17.6777 6.75033C18.1152 7.76421 18.334 8.84755 18.334 10.0003C18.334 11.1531 18.1152 12.2364 17.6777 13.2503C17.2402 14.2642 16.6465 15.1462 15.8965 15.8962C15.1465 16.6462 14.2645 17.2399 13.2507 17.6774C12.2368 18.1149 11.1534 18.3337 10.0007 18.3337ZM12.334 13.5003L9.16732 10.3337V5.83366H10.834V9.66699L13.5007 12.3337L12.334 13.5003Z"
                fill="#8B9096"
              />
            </svg>
            <span>Program Hours | {nursery.programHours}</span>
          </div>
          {nursery.reviewCount > 0 && (
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.5638 11.8753L10.0013 10.3962L12.4388 11.8753L11.793 9.10449L13.9596 7.22949L11.1055 7.00033L10.0013 4.37533L8.89713 7.00033L6.04297 7.22949L8.20964 9.10449L7.5638 11.8753ZM1.66797 18.3337V3.33366C1.66797 2.87533 1.83116 2.48296 2.15755 2.15658C2.48394 1.83019 2.8763 1.66699 3.33464 1.66699H16.668C17.1263 1.66699 17.5187 1.83019 17.8451 2.15658C18.1714 2.48296 18.3346 2.87533 18.3346 3.33366V13.3337C18.3346 13.792 18.1714 14.1844 17.8451 14.5107C17.5187 14.8371 17.1263 15.0003 16.668 15.0003H5.0013L1.66797 18.3337ZM4.29297 13.3337H16.668V3.33366H3.33464V14.2712L4.29297 13.3337Z"
                  fill="#8B9096"
                />
              </svg>
              <span>{nursery.reviewCount} reviews</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
